import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Tooltip,
  TablePagination,
  alpha,
  useTheme,
  Stack,
  Card,
  Container,
  CircularProgress
} from "@mui/material";
import { 
  Notes as NotesIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  ArrowForwardIos as ArrowIcon 
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { fetchAllLeads } from "../api/leads";
import { format } from "date-fns";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export default function MyLeads() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leadsData, setLeadsData] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
  });
  const [sortConfig, setSortConfig] = useState({
    key: "customerName",
    direction: "asc",
  });

  const navigate = useNavigate();

  const getMyLeads = async () => {
    setLoading(true);
    try {
      const data = await fetchAllLeads({ page: page + 1, limit: rowsPerPage });
      if (data.status) {
        setLeadsData(data.data);
        setPagination(data.data.pagination);
      }
    } catch (error) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const isSameKey = prevConfig.key === key;
      const newDirection =
        isSameKey && prevConfig.direction === "asc" ? "desc" : "asc";
      return { key, direction: newDirection };
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "pending":
        return { color: 'warning', label: 'Pending' };
      case "contacted":
        return { color: 'info', label: 'Contacted' };
      case "follow":
        return { color: 'primary', label: 'Follow-up' };
      case "completed":
        return { color: 'success', label: 'Completed' };
      case "denied":
        return { color: 'error', label: 'Denied' };
      case "asigned":
        return { color: 'secondary', label: 'Assigned' };
      default:
        return { color: 'default', label: status };
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedLeads = leadsData?.myLeads
    ? [...leadsData.myLeads].sort((a, b) => {
        if (!sortConfig.key) return 0;
        const aValue = a[sortConfig.key] || "";
        const bValue = b[sortConfig.key] || "";

        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfig.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }

        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();

        if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      })
    : [];

  useEffect(() => {
    getMyLeads();
  }, [page, rowsPerPage]);

  if (loading && !leadsData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h4" sx={{ fontWeight: 900 }}>
              My Payment Leads
            </Typography>
            <Chip 
              label={`${pagination.totalCount} Active`} 
              color="primary" 
              sx={{ fontWeight: 800, borderRadius: 2 }} 
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage and follow up with your assigned payment customers
          </Typography>
        </Box>
      </Box>

      {error ? (
        <Typography color="error" textAlign="center">{error}</Typography>
      ) : (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    { key: "customerName", label: "Customer" },
                    { key: "customerEmail", label: "Contact Info" },
                    { key: "createdAt", label: "Assignment Date" },
                    { key: "notes", label: "Notes" },
                    { key: "status", label: "Status" },
                  ].map((column) => (
                    <TableCell
                      key={column.key}
                      onClick={() => handleSort(column.key)}
                      sx={{
                        fontWeight: 800,
                        backgroundColor: 'grey.50',
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        cursor: "pointer",
                        py: 2,
                        "&:hover": { color: 'primary.main' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {column.label}
                        {sortConfig.key === column.key && (
                          sortConfig.direction === "asc" ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                  ))}
                  <TableCell sx={{ bgcolor: 'grey.50' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedLeads.map((entry, index) => {
                  const statusInfo = getStatusStyle(entry.status);
                  return (
                    <TableRow 
                      key={index} 
                      hover 
                      sx={{ 
                        cursor: 'pointer',
                        '&:last-child td': { border: 0 },
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) }
                      }}
                      onClick={() => navigate(`/payment-lead?id=${entry._id}`)}
                    >
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{entry.customerName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                            <EmailIcon sx={{ fontSize: '0.9rem' }} />
                            <Typography variant="caption">{entry.customerEmail}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                            <PhoneIcon sx={{ fontSize: '0.9rem' }} />
                            <Typography variant="caption">{entry.customerPhone}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {format(new Date(entry.createdAt), "MMM dd, yyyy")}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {format(new Date(entry.createdAt), "hh:mm a")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={entry.notes || "No notes available"} arrow placement="top">
                          <Box 
                            sx={{ 
                              display: 'inline-flex', 
                              p: 1, 
                              borderRadius: 1.5, 
                              bgcolor: entry.notes ? alpha(theme.palette.primary.main, 0.1) : 'grey.100',
                              color: entry.notes ? 'primary.main' : 'text.disabled'
                            }}
                          >
                            <NotesIcon fontSize="small" />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusInfo.label}
                          size="small"
                          color={statusInfo.color}
                          sx={{ fontWeight: 800, fontSize: '0.65rem', borderRadius: 1.5 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <ArrowIcon sx={{ fontSize: '0.8rem', color: 'text.disabled' }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 20, 50]}
            sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
          />
        </Card>
      )}
    </Container>
  );
}

